import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import {
  TOUR_SEEN_KEY,
  TOUR_OPTOUT_KEY,
  TOUR_PROMPT_SESSION_KEY,
} from "./GuidedTour";

/**
 * First-login walkthrough prompt.
 *
 * Shown on the Dashboard after login. Rules:
 *   • Never shows if the founder already completed / started the tour
 *     (localStorage `cpgTourSeen`).
 *   • Never shows again if the founder taps "Don't ask again"
 *     (localStorage `cpgTourOptOut`).
 *   • "Skip for now" only silences the prompt for the current browser
 *     session (sessionStorage) — it re-appears on the next login.
 *   • "Yes, show me around" dispatches `cpg:tour:start`, which the
 *     GuidedTour component listens for. Starting the tour flips
 *     `cpgTourSeen` so this prompt never fires again either.
 */
export default function WalkthroughPrompt() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  // Sequence guard: don't compete with the Community Profile onboarding
  // modal — it's higher-priority actionable data collection. We wait
  // until the user has published/saved/skipped that step before showing
  // the walkthrough offer.
  const directoryOnboardingPending =
    !!user && user.directory_onboarding_completed === false;

  useEffect(() => {
    if (directoryOnboardingPending) return;
    let cancelled = false;
    try {
      if (localStorage.getItem(TOUR_SEEN_KEY)) return;
      if (localStorage.getItem(TOUR_OPTOUT_KEY)) return;
      if (sessionStorage.getItem(TOUR_PROMPT_SESSION_KEY)) return;
    } catch {
      /* private mode — just show it */
    }
    const t = setTimeout(() => {
      if (!cancelled) setOpen(true);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [directoryOnboardingPending]);

  const close = () => setOpen(false);

  const startTour = () => {
    close();
    // Fire on the next tick so the modal transition completes before the
    // Joyride spotlight paints — otherwise the first step can flicker.
    setTimeout(() => {
      window.dispatchEvent(new Event("cpg:tour:start"));
    }, 150);
  };

  const skipForNow = () => {
    try {
      sessionStorage.setItem(TOUR_PROMPT_SESSION_KEY, "1");
    } catch {
      /* private mode */
    }
    close();
  };

  const optOut = () => {
    try {
      localStorage.setItem(TOUR_OPTOUT_KEY, "1");
    } catch {
      /* private mode */
    }
    close();
  };

  if (!open || directoryOnboardingPending) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
      data-testid="walkthrough-prompt"
      role="dialog"
      aria-modal="true"
      aria-labelledby="walkthrough-prompt-title"
    >
      <div
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-sm"
        onClick={skipForNow}
      />

      <div className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        <button
          type="button"
          onClick={skipForNow}
          aria-label="Close"
          className="absolute top-3 right-3 p-1.5 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
          data-testid="walkthrough-prompt-close"
        >
          <X className="w-4 h-4" />
        </button>

        <div
          className="h-1.5 w-full"
          style={{
            background:
              "linear-gradient(135deg, #D81B60 0%, #6A0DAD 55%, #1E40AF 100%)",
          }}
        />

        <div className="p-7">
          <div className="inline-flex items-center gap-2 rounded-full bg-fuchsia-50 text-fuchsia-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" /> First time here
          </div>

          <h2
            id="walkthrough-prompt-title"
            className="mt-4 font-display text-2xl leading-tight text-slate-900"
            style={{ fontFamily: "'Fraunces', Georgia, serif" }}
          >
            Would you like a quick platform walkthrough?
          </h2>

          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            A 60-second tour of your dashboard, modules, databases, and Morgan
            AI — so you know exactly where everything lives.
          </p>

          <div className="mt-6 flex flex-col sm:flex-row-reverse gap-2">
            <button
              type="button"
              onClick={startTour}
              className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-white shadow-md hover:shadow-lg transition"
              style={{
                background:
                  "linear-gradient(135deg, #D81B60 0%, #6A0DAD 100%)",
              }}
              data-testid="walkthrough-prompt-accept"
            >
              Yes, show me around
            </button>
            <button
              type="button"
              onClick={skipForNow}
              className="inline-flex items-center justify-center rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.14em] text-slate-600 border border-slate-200 hover:bg-slate-50 transition"
              data-testid="walkthrough-prompt-skip"
            >
              Skip for now
            </button>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={optOut}
              className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition"
              data-testid="walkthrough-prompt-optout"
            >
              Don&rsquo;t ask me again
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
